import { apiClient } from './apiClient';
import { API_CONFIG } from '../config/api';
import { logInfo, logError } from './logtail';

export interface AudioCommentReply {
  replyId: string;
  authorUserId: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface AudioComment {
  trackId: string;
  fileId: string;
  threadId: string;
  timestampMs: number;
  authorUserId: string;
  text: string;
  createdAt: string;
  updatedAt: string;
  resolved: boolean;
  resolvedByUserId?: string;
  resolvedAt?: string;
  replies: AudioCommentReply[];
}

export const listComments = async (trackId: string, fileId: string): Promise<AudioComment[]> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUDIO_COMMENTS(trackId, fileId));
    logInfo('Audio comments loaded', { trackId, fileId, count: response.data.length });
    return response.data;
  } catch (error) {
    logError('Failed to load audio comments', { trackId, fileId, error });
    throw error;
  }
};

export const createThread = async (trackId: string, fileId: string, timestampMs: number, text: string): Promise<AudioComment> => {
  try {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUDIO_COMMENTS(trackId, fileId), {
      timestampMs,
      text,
    });
    logInfo('Audio comment thread created', { trackId, fileId, timestampMs });
    return response.data;
  } catch (error) {
    logError('Failed to create audio comment thread', { trackId, fileId, error });
    throw error;
  }
};

export const addReply = async (trackId: string, fileId: string, threadId: string, text: string): Promise<void> => {
  try {
    await apiClient.post(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_REPLIES(trackId, fileId, threadId), { text });
    logInfo('Audio comment reply added', { trackId, fileId, threadId });
  } catch (error) {
    logError('Failed to add audio comment reply', { trackId, fileId, threadId, error });
    throw error;
  }
};

export const updateThread = async (trackId: string, fileId: string, threadId: string, text: string): Promise<void> => {
  try {
    await apiClient.patch(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_THREAD(trackId, fileId, threadId), { text });
    logInfo('Audio comment thread updated', { trackId, fileId, threadId });
  } catch (error) {
    logError('Failed to update audio comment thread', { trackId, fileId, threadId, error });
    throw error;
  }
};

export const updateReply = async (trackId: string, fileId: string, threadId: string, replyId: string, text: string): Promise<void> => {
  try {
    await apiClient.patch(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_REPLY(trackId, fileId, threadId, replyId), { text });
    logInfo('Audio comment reply updated', { trackId, fileId, threadId, replyId });
  } catch (error) {
    logError('Failed to update audio comment reply', { trackId, fileId, threadId, replyId, error });
    throw error;
  }
};

export const resolveThread = async (trackId: string, fileId: string, threadId: string, resolved: boolean): Promise<void> => {
  try {
    await apiClient.post(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_RESOLVE(trackId, fileId, threadId), { resolved });
    logInfo('Audio comment thread resolved', { trackId, fileId, threadId, resolved });
  } catch (error) {
    logError('Failed to resolve audio comment thread', { trackId, fileId, threadId, error });
    throw error;
  }
};

export const deleteThread = async (trackId: string, fileId: string, threadId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_THREAD(trackId, fileId, threadId));
    logInfo('Audio comment thread deleted', { trackId, fileId, threadId });
  } catch (error) {
    logError('Failed to delete audio comment thread', { trackId, fileId, threadId, error });
    throw error;
  }
};

export const deleteReply = async (trackId: string, fileId: string, threadId: string, replyId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.AUDIO_COMMENT_REPLY(trackId, fileId, threadId, replyId));
    logInfo('Audio comment reply deleted', { trackId, fileId, threadId, replyId });
  } catch (error) {
    logError('Failed to delete audio comment reply', { trackId, fileId, threadId, replyId, error });
    throw error;
  }
};

